(() => {
  if (window.__olasSmartRecommendationsLoaded) return;
  window.__olasSmartRecommendationsLoaded = true;

  const toHandle = value => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const pipeContains = (pipeString, value) => {
    const handle = toHandle(value);
    if (!handle) return false;
    return String(pipeString || '').includes(`|${handle}|`);
  };

  const getPairStem = value => {
    const ignoredTokens = new Set([
      'bikini', 'swim', 'swimwear', 'womens', 'women',
      'top', 'tops', 'bottom', 'bottoms', 'brief', 'briefs'
    ]);

    return toHandle(value)
      .split('-')
      .filter(token => token && !ignoredTokens.has(token))
      .join('-');
  };

  const getOptionWrappers = selector => Array.from(selector.querySelectorAll('[data-input-wrapper]'));

  const getColorWrapper = selector => {
    const wrappers = getOptionWrappers(selector);

    return wrappers.find(wrapper => {
      const idHandle = toHandle(wrapper.id || '');
      const legend = wrapper.querySelector('legend');
      const legendHandle = toHandle(legend ? legend.textContent : '');

      return idHandle.includes('color') ||
        idHandle.includes('colour') ||
        legendHandle === 'color' ||
        legendHandle === 'colour';
    }) || null;
  };

  const getSelectedColor = selector => {
    const wrapper = getColorWrapper(selector);
    if (!wrapper) return '';

    const checked = wrapper.querySelector('input[type="radio"]:checked');
    if (checked) return checked.value || '';

    const select = wrapper.querySelector('select');
    if (select) return select.value || '';

    return '';
  };

  const parseVariants = slide => {
    if (slide.__olasVariants) return slide.__olasVariants;

    const script = slide.querySelector('.js-olas-smart-recommendation-variants');
    if (!script) {
      slide.__olasVariants = [];
      return slide.__olasVariants;
    }

    try {
      slide.__olasVariants = JSON.parse(script.textContent || '[]');
    } catch (error) {
      console.warn('[Las Olas] Could not parse smart recommendation variant data.', error);
      slide.__olasVariants = [];
    }

    return slide.__olasVariants;
  };



  const parseColorMedia = slide => {
    if (slide.__olasColorMedia) return slide.__olasColorMedia;

    const script = slide.querySelector('.js-olas-smart-recommendation-media');
    if (!script) {
      slide.__olasColorMedia = [];
      return slide.__olasColorMedia;
    }

    try {
      slide.__olasColorMedia = JSON.parse(script.textContent || '[]');
    } catch (error) {
      console.warn('[Las Olas] Could not parse smart recommendation media data.', error);
      slide.__olasColorMedia = [];
    }

    return slide.__olasColorMedia;
  };

  const findBestMediaForColor = (mediaItems, colorHandle) => {
    if (!mediaItems.length || !colorHandle) return null;
    return mediaItems.find(item => item.colorHandle === colorHandle) || null;
  };

  const findBestVariantForColor = (variants, colorHandle) => {
    if (!variants.length || !colorHandle) return null;

    const matches = variants.filter(variant => variant.colorHandle === colorHandle);
    if (!matches.length) return null;

    return matches.find(variant => variant.available) || matches[0] || null;
  };

  class OlasSmartRecommendations extends HTMLElement {
    constructor() {
      super();

      this.slider = this.querySelector('[data-olas-smart-slider]');
      this.wrapper = this.querySelector('[data-olas-smart-wrapper]');
      this.emptyMessage = this.querySelector('[data-olas-smart-empty]');
      this.buttons = this.querySelector('[data-olas-smart-buttons]');
      this.maxProducts = Number(this.dataset.maxProducts || 8);
      this.fillFromSource = this.dataset.fillFromSource === 'true';
      this.imageHoverEnabled = this.dataset.imageHoverEnabled !== 'false';
      this.showUnavailableProducts = this.dataset.showUnavailableProducts === 'true';
      this.currentProductId = this.dataset.currentProductId || '';
      this.currentProductTitle = this.dataset.currentProductTitle || '';
      this.currentPairStem = getPairStem(this.currentProductTitle);
      this.currentVendorValue = this.dataset.currentVendor || '';
      this.currentVendor = this.dataset.currentVendorHandle || '';
      this.currentType = this.dataset.currentTypeHandle || '';
      this.currentPiece = this.dataset.currentPieceGroup || 'other';
      this.oppositePiece = this.dataset.oppositePieceGroup || '';
      this.activeColorValue = this.dataset.initialColor || '';
      this.activeColor = toHandle(this.dataset.initialColorHandle || this.dataset.initialColor || '');
      this.colorFilterParam = this.dataset.colorFilterParam || 'filter.v.option.color';
      this.sourceCollectionUrl = this.dataset.sourceCollectionUrl || '/collections/all';
      this.topTypeHandles = this.dataset.topTypeHandles || '';
      this.bottomTypeHandles = this.dataset.bottomTypeHandles || '';
      if (this.currentPiece === 'other') {
        this.currentPiece = this.classifyPieceGroup(this.currentType, this.currentProductTitle);
        this.oppositePiece = this.currentPiece === 'top'
          ? 'bottom'
          : this.currentPiece === 'bottom' ? 'top' : '';
      }
      this.allSlides = [];
      this.fallbackSlides = [];
      this.candidateCache = new Map();
      this.requestController = null;
      this.requestSequence = 0;
      this.swiper = null;
      this.hasInitialized = false;
      this.handleDocumentChange = this.handleDocumentChange.bind(this);
      this.handleMediaRefresh = this.handleMediaRefresh.bind(this);
    }

    connectedCallback() {
      if (this.hasInitialized || !this.wrapper) return;

      this.fallbackSlides = Array.from(this.wrapper.querySelectorAll('.js-olas-smart-recommendation-slide'));
      this.applyCandidateMetadata(this.fallbackSlides);
      this.allSlides = this.fallbackSlides;
      this.hasInitialized = true;
      this.render();
      this.loadFilteredCandidates(this.activeColorValue);

      document.addEventListener('change', this.handleDocumentChange);
      document.addEventListener('olas:product-media-refreshed', this.handleMediaRefresh);
      document.addEventListener('shopify:section:load', () => this.render());
    }

    disconnectedCallback() {
      document.removeEventListener('change', this.handleDocumentChange);
      document.removeEventListener('olas:product-media-refreshed', this.handleMediaRefresh);
      this.requestController?.abort();
      this.destroySlider();
    }

    handleDocumentChange(event) {
      const selector = event.target.closest('product-selector');
      if (!selector) return;

      window.setTimeout(() => {
        const currentSelector = document.querySelector(`product-selector[data-section-id="${selector.dataset.sectionId}"]`) || selector;
        const selectedColor = getSelectedColor(currentSelector);
        const selectedColorHandle = toHandle(selectedColor);

        if (!selectedColorHandle || selectedColorHandle === this.activeColor) return;

        this.activeColorValue = selectedColor;
        this.activeColor = selectedColorHandle;
        this.loadFilteredCandidates(selectedColor);
      }, 0);
    }

    handleMediaRefresh(event) {
      const selectedColor = event.detail?.selectedColor || '';
      const selectedColorHandle = toHandle(selectedColor);
      if (!selectedColorHandle || selectedColorHandle === this.activeColor) return;

      this.activeColorValue = selectedColor;
      this.activeColor = selectedColorHandle;
      this.loadFilteredCandidates(selectedColor);
    }

    classifyPieceGroup(typeHandle, productTitle = '') {
      const normalizedType = toHandle(typeHandle);
      const titleTokens = toHandle(productTitle).split('-');

      if (
        pipeContains(this.topTypeHandles, normalizedType) ||
        normalizedType.includes('top') ||
        titleTokens.includes('top') ||
        titleTokens.includes('tops')
      ) return 'top';

      if (
        pipeContains(this.bottomTypeHandles, normalizedType) ||
        normalizedType.includes('bottom') ||
        titleTokens.includes('bottom') ||
        titleTokens.includes('bottoms')
      ) return 'bottom';

      return 'other';
    }

    applyCandidateMetadata(slides) {
      slides.forEach(slide => {
        slide.dataset.pieceGroup = this.classifyPieceGroup(
          slide.dataset.typeHandle || '',
          slide.dataset.productTitle || ''
        );
      });
    }

    buildFilteredCandidateUrl(colorValue) {
      const url = new URL(this.sourceCollectionUrl, window.location.origin);
      url.searchParams.set('view', 'olas-smart-recommendations');
      url.searchParams.set('filter.p.vendor', this.currentVendorValue);
      url.searchParams.set(this.colorFilterParam, colorValue);
      return url.toString();
    }

    async loadFilteredCandidates(colorValue) {
      const colorHandle = toHandle(colorValue);
      if (!colorHandle || !this.currentVendorValue || !this.sourceCollectionUrl) {
        this.allSlides = this.fallbackSlides;
        this.render();
        return;
      }

      const cacheKey = `${this.currentVendor}|${colorHandle}`;
      const cachedSlides = this.candidateCache.get(cacheKey);
      if (cachedSlides) {
        this.allSlides = cachedSlides.length ? cachedSlides : this.fallbackSlides;
        this.render();
        return;
      }

      this.requestController?.abort();
      this.requestController = new AbortController();
      const requestSequence = ++this.requestSequence;
      this.classList.add('is-loading-candidates');

      try {
        const response = await fetch(this.buildFilteredCandidateUrl(colorValue), {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          signal: this.requestController.signal
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const feed = documentFragment.querySelector('[data-olas-candidate-feed]');
        const slides = feed
          ? Array.from(feed.querySelectorAll('.js-olas-smart-recommendation-slide'))
              .filter(slide => slide.dataset.productId !== this.currentProductId)
          : [];

        this.applyCandidateMetadata(slides);
        this.candidateCache.set(cacheKey, slides);

        if (requestSequence !== this.requestSequence) return;

        this.allSlides = slides.length ? slides : this.fallbackSlides;
        this.render();
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.warn('[Las Olas] Could not load filtered smart recommendations; using the fallback pool.', error);
        }

        if (requestSequence === this.requestSequence) {
          this.allSlides = this.fallbackSlides;
          this.render();
        }
      } finally {
        if (requestSequence === this.requestSequence) {
          this.classList.remove('is-loading-candidates');
        }
      }
    }

    getRank(slide) {
      const hasColorOption = pipeContains(slide.dataset.colors, this.activeColor);
      const colorVariants = parseVariants(slide)
        .filter(variant => variant.colorHandle === this.activeColor);
      const hasAvailableColorVariant = colorVariants.some(variant => variant.available);
      const hasSameColor = hasColorOption && (
        this.showUnavailableProducts || colorVariants.length === 0 || hasAvailableColorVariant
      );
      const sameVendor = slide.dataset.vendorHandle === this.currentVendor;
      const sameType = this.currentType && slide.dataset.typeHandle === this.currentType;
      const oppositePiece = this.oppositePiece && slide.dataset.pieceGroup === this.oppositePiece;
      const samePiece = this.currentPiece !== 'other' && slide.dataset.pieceGroup === this.currentPiece;
      const exactPair = this.currentPairStem &&
        getPairStem(slide.dataset.productTitle || '') === this.currentPairStem;

      if (hasColorOption && !this.showUnavailableProducts && colorVariants.length > 0 && !hasAvailableColorVariant) {
        return 999;
      }

      if (sameVendor && hasSameColor && oppositePiece && exactPair) return 5;
      if (sameVendor && hasSameColor && oppositePiece) return 10;
      if (sameVendor && hasSameColor) return 20;
      if (hasSameColor && oppositePiece) return 30;
      if (sameType) return 40;
      if (samePiece) return 50;

      return this.fillFromSource ? 90 : 999;
    }

    updateCardForColor(slide) {
      const variants = parseVariants(slide);
      const colorMedia = findBestMediaForColor(parseColorMedia(slide), this.activeColor);
      const variant = findBestVariantForColor(variants, this.activeColor);
      const mediaLink = slide.querySelector('[data-olas-card-link]');
      const contentLink = slide.querySelector('[data-olas-card-content-link]');
      const image = slide.querySelector('.js-olas-card-image');
      let hoverImage = slide.querySelector('.js-olas-card-hover-image');
      const media = slide.querySelector('.olas-smart-recommendations__media');
      const fallbackUrl = slide.dataset.fallbackUrl || '#';
      const fallbackImage = slide.dataset.fallbackImage || '';
      const nextUrl = variant?.url || fallbackUrl;
      const nextImage = colorMedia?.image || variant?.image || fallbackImage;
      const nextHoverImage = this.imageHoverEnabled ? colorMedia?.hoverImage || '' : '';

      if (mediaLink) mediaLink.href = nextUrl;
      if (contentLink) contentLink.href = nextUrl;

      if (image && nextImage && image.getAttribute('src') !== nextImage) {
        media?.classList.add('is-updating');
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.src = nextImage;
        image.addEventListener('load', () => media?.classList.remove('is-updating'), { once: true });
      } else {
        media?.classList.remove('is-updating');
      }

      if (!this.imageHoverEnabled) {
        media?.classList.remove('has-hover');
        if (hoverImage) {
          hoverImage.removeAttribute('srcset');
          hoverImage.removeAttribute('sizes');
          hoverImage.removeAttribute('src');
          hoverImage.setAttribute('aria-hidden', 'true');
        }
        return;
      }

      if (nextHoverImage) {
        if (!hoverImage && media) {
          hoverImage = document.createElement('img');
          hoverImage.className = 'js-olas-card-hover-image hover-img';
          hoverImage.loading = 'lazy';
          hoverImage.setAttribute('aria-hidden', 'true');
          media.appendChild(hoverImage);
        }

        if (hoverImage) {
          hoverImage.removeAttribute('srcset');
          hoverImage.removeAttribute('sizes');
          hoverImage.alt = image?.alt || '';
          if (hoverImage.getAttribute('src') !== nextHoverImage) {
            hoverImage.src = nextHoverImage;
          }
          media?.classList.add('has-hover');
        }
      } else {
        media?.classList.remove('has-hover');
        if (hoverImage) {
          hoverImage.removeAttribute('srcset');
          hoverImage.removeAttribute('sizes');
          hoverImage.removeAttribute('src');
          hoverImage.setAttribute('aria-hidden', 'true');
        }
      }
    }

    getRankedSlides() {
      return this.allSlides
        .map((slide, originalIndex) => ({
          slide,
          originalIndex,
          rank: this.getRank(slide)
        }))
        .filter(item => item.rank < 999)
        .sort((a, b) => {
          if (a.rank !== b.rank) return a.rank - b.rank;
          return a.originalIndex - b.originalIndex;
        })
        .slice(0, this.maxProducts);
    }

    render() {
      if (!this.wrapper) return;

      const rankedSlides = this.getRankedSlides();
      this.destroySlider();
      this.wrapper.innerHTML = '';

      rankedSlides.forEach((item, index) => {
        const slide = item.slide;
        slide.classList.remove('is-hidden');
        slide.style.setProperty('--olas-recommendation-order', index + 1);
        slide.dataset.olasRank = item.rank;
        this.updateCardForColor(slide);
        this.wrapper.appendChild(slide);
      });

      const isEmpty = rankedSlides.length === 0;
      this.slider?.classList.toggle('is-empty', isEmpty);
      this.emptyMessage?.classList.toggle('hidden', !isEmpty);
      this.buttons?.classList.toggle('hidden', isEmpty || rankedSlides.length <= 4);

      if (!isEmpty) this.initSlider();
    }

    initSlider() {
      if (!this.slider || typeof Swiper === 'undefined') return;

      let swiperOptions = {};
      try {
        swiperOptions = JSON.parse(this.slider.getAttribute('data-swiper-options') || '{}');
      } catch (error) {
        swiperOptions = {};
      }

      this.swiper = new Swiper(this.slider, {
        a11y: false,
        threshold: 3,
        spaceBetween: swiperOptions.spaceBetweenMobile || 2,
        slidesPerView: 'auto',
        resistanceRatio: 0.72,
        navigation: {
          nextEl: this.slider.querySelector('.swiper-button--next'),
          prevEl: this.slider.querySelector('.swiper-button--prev')
        },
        breakpoints: {
          750: {
            slidesPerView: 3,
            spaceBetween: swiperOptions.spaceBetweenDesktop || 2
          },
          1100: {
            slidesPerView: 4,
            spaceBetween: swiperOptions.spaceBetweenDesktop || 2
          }
        }
      });
    }

    destroySlider() {
      if (this.swiper && !this.swiper.destroyed) {
        this.swiper.destroy(true, true);
      }
      this.swiper = null;
    }
  }

  if (!customElements.get('olas-smart-recommendations')) {
    customElements.define('olas-smart-recommendations', OlasSmartRecommendations);
  }
})();
